import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { 
  Users,
  Eye,
  Trash2,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Calendar,
  Target,
  BarChart3,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../ui/use-toast';
import axios from 'axios';

const ResultadosIndividuais = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [resultados, setResultados] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    fit_alto: 0,
    fit_medio: 0,
    fit_baixo: 0,
    score_medio: 0
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadResultados();
  }, []);

  const loadResultados = async () => {
    try {
      setLoading(true);
      
      // Buscar do mesmo endpoint que "Gerenciar Avaliações" usa
      const response = await axios.get(
        `${backendUrl}/api/fit-cultural/concluidas`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      console.log('📊 ===== DADOS BRUTOS DO BACKEND =====');
      console.log('Total de resultados:', response.data.length);
      response.data.forEach((r, idx) => {
        console.log(`\n[${idx + 1}] ${r.colaborador_nome} - ${r.avaliacao_nome}`);
        console.log('  ifc_calculado:', r.ifc_calculado, typeof r.ifc_calculado);
        console.log('  ifc_bruto:', r.ifc_bruto, typeof r.ifc_bruto);
        console.log('  fator_confiabilidade:', r.fator_confiabilidade, typeof r.fator_confiabilidade);
        console.log('  Todos os campos:', Object.keys(r));
      });
      console.log('=====================================');
      
      setResultados(response.data);
      calcularStats(response.data);
      
    } catch (error) {
      console.error('❌ Erro ao carregar resultados:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar resultados das avaliações",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const calcularStats = (dados) => {
    if (dados.length === 0) {
      setStats({ total: 0, fit_alto: 0, fit_medio: 0, fit_baixo: 0, score_medio: 0 });
      return;
    }

    // Os valores vêm como decimal (0.552 = 55.2%), então multiplicamos por 100
    const scores = dados.map(r => (r.ifc_calculado || 0) * 100);
    const total = dados.length;
    
    const fit_alto = scores.filter(s => s > 60).length;
    const fit_medio = scores.filter(s => s > 30 && s <= 60).length;
    const fit_baixo = scores.filter(s => s <= 30).length;
    const score_medio = scores.reduce((a, b) => a + b, 0) / total;

    setStats({
      total,
      fit_alto,
      fit_medio,
      fit_baixo,
      score_medio: Math.round(score_medio)
    });
  };

  const handleExcluir = async (id, nome) => {
    if (!window.confirm(`Tem certeza que deseja excluir a avaliação de "${nome}"?`)) return;
    try {
      await axios.delete(
        `${backendUrl}/api/fit-cultural/avaliacoes-aplicadas/${id}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast({
        title: "Avaliação Excluída",
        description: "A avaliação foi removida com sucesso",
      });
      loadResultados();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Erro ao excluir avaliação",
        variant: "destructive"
      });
    }
  };

  const getClassificacao = (score) => {
    if (score <= 30) {
      return { 
        label: 'Fit Baixo', 
        color: 'bg-red-100 text-red-800 border-red-300',
        icon: TrendingDown 
      };
    } else if (score <= 60) {
      return { 
        label: 'Fit Médio', 
        color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        icon: AlertCircle 
      };
    } else {
      return { 
        label: 'Fit Alto', 
        color: 'bg-green-100 text-green-800 border-green-300',
        icon: TrendingUp 
      };
    }
  };

  const formatarData = (dataString) => {
    if (!dataString) return 'N/A';
    try {
      return new Date(dataString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Carregando resultados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Resultados Individuais</h1>
          <p className="text-gray-600 mt-1">Avaliações Fit Cultural finalizadas</p>
        </div>
        <Button onClick={loadResultados} variant="outline" className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </Button>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
            <p className="text-xs text-gray-500 mt-1">Avaliações</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Fit Alto</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{stats.fit_alto}</div>
            <p className="text-xs text-gray-500 mt-1">&gt; 60%</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Fit Médio</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-yellow-600">{stats.fit_medio}</div>
            <p className="text-xs text-gray-500 mt-1">31-60%</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Fit Baixo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">{stats.fit_baixo}</div>
            <p className="text-xs text-gray-500 mt-1">0-30%</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Score Médio</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{stats.score_medio}%</div>
            <Progress value={stats.score_medio} className="mt-2 h-2" />
          </CardContent>
        </Card>
      </div>

      {/* Tabela de Resultados */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Resultados ({resultados.length})
          </CardTitle>
          <CardDescription>
            Listagem de todas as avaliações finalizadas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {resultados.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhuma avaliação finalizada
              </h3>
              <p className="text-gray-600 mb-4">
                As avaliações aparecerão aqui quando os colaboradores responderem
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto text-left">
                <p className="text-sm text-blue-800">
                  <strong>💡 Dica:</strong> Para ver resultados aqui:
                </p>
                <ol className="text-sm text-blue-700 mt-2 ml-4 space-y-1">
                  <li>1. Aplique avaliações em "Gerenciar Avaliações"</li>
                  <li>2. Colaboradores respondem em "Minhas Avaliações"</li>
                  <li>3. Resultados aparecem automaticamente aqui</li>
                </ol>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Colaborador</TableHead>
                    <TableHead>Avaliação</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-center">Score (%)</TableHead>
                    <TableHead className="text-center">Classificação</TableHead>
                    <TableHead>Data Finalização</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resultados.map((resultado) => {
                    // Valores vêm como decimal, multiplicar por 100 para porcentagem
                    const scorePercent = (resultado.ifc_calculado || 0) * 100;
                    const classificacao = getClassificacao(scorePercent);
                    const Icon = classificacao.icon;
                    
                    return (
                      <TableRow key={resultado.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-gray-400" />
                            {resultado.colaborador_nome || 'N/A'}
                          </div>
                        </TableCell>
                        <TableCell>
                          {resultado.avaliacao_nome || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            Funcionário
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col items-center">
                            <span className="text-lg font-bold text-gray-900">
                              {Math.round(scorePercent)}%
                            </span>
                            <Progress 
                              value={scorePercent} 
                              className="w-20 h-1 mt-1"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={classificacao.color}>
                            <Icon className="w-3 h-3 mr-1" />
                            {classificacao.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            {formatarData(resultado.concluida_em)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                const bruto = ((resultado.ifc_bruto || 0) * 100).toFixed(1);
                                const confiabilidade = ((resultado.fator_confiabilidade || 0) * 100).toFixed(1);
                                toast({
                                  title: "Detalhes da Avaliação",
                                  description: `IFC Bruto: ${bruto}% | Confiabilidade: ${confiabilidade}%`,
                                });
                              }}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Ver Detalhes
                            </Button>
                            {user?.is_admin && (
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleExcluir(resultado.id, resultado.colaborador_nome)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResultadosIndividuais;
